import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Backbone with ES6",
  "description": "",
  "published": "2016-09-01T00:00:00.000Z",
  "redirect_from": ["/blog/backbone-with-es6/swizec/6994"],
  "categories": "backbone, es6, Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`I don’t know about you but I’m amazed by how many solutions there are for writing maintainable apps these days. Remember 7 years ago.`}</p>{`— Dan (@dan_abramov) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/dan_abramov/status/770684302568849408"
      }}>{`August 30, 2016`}</a></blockquote>
    <p>{`Ah yes, Dan Abramov. How right he is! Building maintainable apps for the browser has never been easier or more fun. Life is great.`}</p>
    <p>{`That is, if you’re Dan Abramov and you spend your days building the future of web technology. I don’t know how he built that life, but I’m impressed.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`and here I am in the real world, looking at all these solutions, and thinking "Man, 7 years from now is gonna be nice"`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/770686430427291648"
      }}>{`August 30, 2016`}</a></blockquote>
    <p>{`The real world is not like that. For us, building `}<em parentName="p">{`businesses`}</em>{` is the important part, not building technologies. We’re building technology in the service of `}<em parentName="p">{`business`}</em>{` innovation.`}</p>
    <p>{`Unless it’s a weekend hack project, or a fun toy for the soul, we don’t get to throw everything away and re-engineer from scratch every 6 months when new libraries become available. We don’t even get to do it every year.`}</p>
    <p>{`If you can’t make a business case for it, it doesn’t happen.`}</p>
    <p>{`But we do what we can. We evolve rather than rewrite. Baby step after baby step, our tech stacks become better and more fun to use. They are driven by business needs rather than creature comforts.`}</p>
    <p>{`We’re highly paid professionals, after all. You wouldn’t expect a race car driver to only drive cars with air conditioning, would you? (Hint: There’s no such thing as AC in race cars. It’s too heavy.)`}</p>
    <p>{`All that to say that if you’re still using Backbone, you too can use ES6 classes! ?`}</p>
    <p>{`/giphy partyhard`}</p>
    <h2 {...{
      "id": "heres-how-you-do-it"
    }}>{`Here’s how you do it`}</h2>
    <p>{`Let’s take a basic Backbone View. It renders as a `}<inlineCode parentName="p">{`div`}</inlineCode>{` element, uses a template, and responds to user events. A click counter, for example.`}</p>
    <p>{`In traditional Backbone, it would look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var Counter = Backbone.View.extend({
  tagName: "div",

  model: new Backbone.Model(),

  template: Handlebars.compile("{count} <button>++</button>"),

  events: {
    "click button": "buttonClick",
  },

  initialize: function () {
    this.model.set({ count: 0 });

    this.listenTo(this.model, "change", this.render);
  },

  render: function () {
    this.$el.html(this.template(this.model.attributes));
  },

  buttonClick: function () {
    this.model.set({ count: this.model.get("count") + 1 });
  },
});
`}</code></pre>
    <p>{`Cool, huh? Don’t worry if you don’t know what any of that means; you’re just not the target audience for this article. :)`}</p>
    <p>{`The quick explanation is that we create a View backed by an anonymous Model. The `}<inlineCode parentName="p">{`model`}</inlineCode>{` holds a `}<inlineCode parentName="p">{`count`}</inlineCode>{` value, we listen for `}<inlineCode parentName="p">{`click`}</inlineCode>{` events on the `}<inlineCode parentName="p">{`button`}</inlineCode>{` element, and we increase the `}<inlineCode parentName="p">{`count`}</inlineCode>{` when they happen. And we listen for `}<inlineCode parentName="p">{`change`}</inlineCode>{` events on the model to decide when to re-render.`}</p>
    <p>{`If this sounds similar to React, that’s because it is. Backbone was, and still is, very close to React in its core ideas. React just happens to implement them better and make it easier to better architect your app.`}</p>
    <p>{`That same View would look like this in ES6:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Counter extends Backbone.View {
  constructor() {
    super();

    this.model = new Backbone.Model({ count: 0 });
    this.tagName = "div";
    this.template = ({ count }) => \`\${count} <button>++</button>\`;
    this.events = {
      "click button": "buttonClick",
    };
    this.listenTo(this.model, "change", this.render);
  }

  render() {
    this.$el.html(this.template(this.model.attributes));
  }

  buttonClick() {
    this.model.set({ count: this.model.get("count") + 1 });
  }
}
`}</code></pre>
    <p>{`We used `}<inlineCode parentName="p">{`constructor`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`initialize`}</inlineCode>{`, and we moved all of the properties into that function. We also used ES6 template strings instead of Handlebars for templating, and we didn’t have to write `}<inlineCode parentName="p">{`function`}</inlineCode>{` even once.`}</p>
    <p>{`It’s the same amount of code, the same amount of logic, a marginal improvement in styling, and the dubious usefulness of subclassing. Big whoop.`}</p>
    <p>{`Every other part of your codebase, new or old, can use this View the same as a traditional ES5 Backbone View. Or it can subclass. That part is easier.`}</p>
    <h2 {...{
      "id": "but-why-do-this"
    }}>{`But… why do this?`}</h2>
    <p>{`It feels good.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      